import React, { useState, useEffect, useRef } from 'react';

// 부드러운 페이드 애니메이션을 위한 컴포넌트
const FadeAnimation = ({
  children,
  type = 'fade-up',
  duration = 800,
  easing = 'ease',
  delay = 0,
  offset = 120,
  triggerOnce = true,
}) => {
  const [isInView, setIsInView] = useState(false);
  const wrapperRef = useRef(null);
  const innerRef = useRef(null);

  useEffect(() => {
    const wrapperElement = wrapperRef.current;
    const innerElement = innerRef.current;

    if (!wrapperElement || !innerElement) return;

    // 초기 상태 설정 - 내부 요소에만 애니메이션 적용
    innerElement.style.opacity = '0';
    innerElement.style.transition = `opacity ${
      duration / 1000
    }s ${easing}, transform ${duration / 1000}s ${easing}`;

    if (type === 'fade-up') {
      innerElement.style.transform = 'translate3d(0, 50px, 0)';
    } else if (type === 'fade-down') {
      innerElement.style.transform = 'translate3d(0, -50px, 0)';
    } else if (type === 'fade-left') {
      innerElement.style.transform = 'translate3d(50px, 0, 0)';
    } else if (type === 'fade-right') {
      innerElement.style.transform = 'translate3d(-50px, 0, 0)';
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          // 지연 시간 설정
          setTimeout(() => {
            // 요소가 화면에 들어왔을 때 스타일 변경
            if (innerElement) {
              innerElement.style.opacity = '1';
              innerElement.style.transform = 'translateZ(0)';
              setIsInView(true);

              // 애니메이션이 완료된 후 스타일 속성 제거
              if (triggerOnce) {
                const cleanupTimeout = setTimeout(() => {
                  innerElement.style.transform = '';
                  innerElement.style.transition = '';
                  innerElement.style.willChange = '';
                }, duration + 50);

                return () => clearTimeout(cleanupTimeout);
              }
            }
          }, delay);

          // triggerOnce가 true면 한 번만 실행후 observer 해제
          if (triggerOnce) {
            observer.unobserve(wrapperElement);
          }
        } else if (!triggerOnce && isInView) {
          // 요소가 화면에서 나갔을 때, triggerOnce가 false인 경우만 초기화
          innerElement.style.opacity = '0';

          if (type === 'fade-up') {
            innerElement.style.transform = 'translate3d(0, 50px, 0)';
          } else if (type === 'fade-down') {
            innerElement.style.transform = 'translate3d(0, -50px, 0)';
          } else if (type === 'fade-left') {
            innerElement.style.transform = 'translate3d(50px, 0, 0)';
          } else if (type === 'fade-right') {
            innerElement.style.transform = 'translate3d(-50px, 0, 0)';
          }

          setIsInView(false);
        }
      },
      {
        root: null,
        rootMargin: `0px 0px -${offset}px 0px`,
        threshold: 0.1, // 10% 정도 보일 때 트리거
      },
    );

    observer.observe(wrapperElement);

    return () => {
      if (wrapperElement) {
        observer.unobserve(wrapperElement);
      }
    };
  }, [type, duration, easing, delay, offset, triggerOnce, isInView]);

  // 중첩 구조를 사용하여 애니메이션 효과가 자식 컴포넌트에 영향을 주지 않도록 함
  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <div ref={innerRef} style={{ width: '100%', height: '100%' }}>
        {children}
      </div>
    </div>
  );
};

export default FadeAnimation;
